import { useState, useCallback } from 'react';
import receiptService from '../services/receiptService';
import type { Transaction, PrintResult } from '../services/receiptService';

interface UseReceiptReturn {
  isLoading: boolean;
  error: string | null;
  printReceipt: (transaction: Transaction) => Promise<PrintResult | null>;
  generatePDF: (transaction: Transaction) => Promise<void>;
  testPrint: () => Promise<void>;
  clearError: () => void;
}

export const useReceipt = (): UseReceiptReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isPrinting, setIsPrinting] = useState(false); // 重複印刷防止フラグ

  const printReceipt = useCallback(async (transaction: Transaction): Promise<PrintResult | null> => {
    // 重複印刷防止
    if (isPrinting) {
      console.log('Already printing, skipping duplicate request');
      return { success: false, error: 'Already printing' };
    }
    
    setIsPrinting(true);
    setIsLoading(true);
    setError(null);
    
    try {
      // プリンター設定を取得
      const settings = localStorage.getItem('printerSettings');
      const printerType = settings ? JSON.parse(settings).type : 'network';
      
      // 印刷実行（自動フォールバック付き）
      const result = await receiptService.printWithFallback(transaction);
      
      // 成功した場合の処理
      if (result.success) {
        // HTMLバックアップが作成された場合
        if (result.method === 'html' && result.fileName) {
          // HTMLファイルが生成された場合の処理
          const downloadHTML = window.confirm(
            'プリンターに接続できませんでした。\nHTMLレシートをダウンロードしますか？'
          );
          
          if (downloadHTML) {
            // バックエンドから直接ダウンロード（新しいファイルは作成しない）
            const apiHost = window.location.hostname;
            const downloadUrl = `http://${apiHost}:3001/api/receipt/download/${result.fileName}`;
            window.open(downloadUrl, '_blank');
          }
        }
      } else if (result.error) {
        // エラーの場合でもHTMLが生成されていればダウンロード可能
        console.error('Print error:', result.error);
        setError(result.error);
      }
      
      // オフライン時はローカルに保存
      if (!navigator.onLine) {
        receiptService.saveReceiptToLocal(transaction);
        setError('オフラインです。レシートデータを保存しました。');
      }
      
      return result;
    } catch (err: any) {
      setError(err.message || 'レシート印刷に失敗しました');
      
      // エラー時もローカルに保存
      receiptService.saveReceiptToLocal(transaction);
      
      return null;
    } finally {
      setIsLoading(false);
      // 印刷完了後、少し待ってからフラグをリセット
      setTimeout(() => setIsPrinting(false), 1000);
    }
  }, [isPrinting]);

  const generatePDF = useCallback(async (transaction: Transaction): Promise<void> => {
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await receiptService.generatePDF(transaction);
      
      if (result.success && result.fileName) {
        await receiptService.downloadPDF(result.fileName);
      } else {
        throw new Error('PDF生成に失敗しました');
      }
    } catch (err: any) {
      setError(err.message || 'PDF生成に失敗しました');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const testPrint = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setError(null);
    
    try {
      const settings = localStorage.getItem('printerSettings');
      const printerType = settings ? JSON.parse(settings).type : 'network';
      
      const result = await receiptService.testPrint(printerType);
      
      if (!result.success) {
        throw new Error(result.error || 'テスト印刷に失敗しました');
      }
    } catch (err: any) {
      setError(err.message || 'テスト印刷に失敗しました');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    isLoading,
    error,
    printReceipt,
    generatePDF,
    testPrint,
    clearError
  };
};